import React, { useCallback, useEffect, useState } from 'react';
import {
  Activity, Pause, SlidersHorizontal, Power, Loader2, TrendingUp,
} from 'lucide-react';
import { useStrategyLabStore } from '../../stores/strategyLabStore';
import { strategyLabApi } from '../../lib/api';
import type { Deployment, DeploymentPosition } from '../../types/strategyLab';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Progress } from '../ui/progress';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import AdjustDeploymentDialog from './AdjustDeploymentDialog';
import RecommendationCard from './RecommendationCard';
import StrategyDecisionMonitor from './StrategyDecisionMonitor';
import { fmtUsd, fmtSignedUsd, fmtPct, pnlColorClass } from './format';

interface DeploymentCardProps {
  deployment: Deployment;
}

const POSITIONS_POLL_MS = 15000;

interface StatusMeta { label: string; tone: string; dot: string; running: boolean; }

/** Backend returns running/paused/stopped/budget_halted/loss_halted/error;
 *  the frontend union also carries active/halted/undeployed. Normalize both. */
function statusMeta(status: string): StatusMeta {
  switch (status) {
    case 'running':
    case 'active':
      return { label: 'Active', tone: 'text-emerald-500 border-emerald-500/40 bg-emerald-500/10', dot: 'bg-emerald-500', running: true };
    case 'paused':
      return { label: 'Paused', tone: 'text-amber-500 border-amber-500/40 bg-amber-500/10', dot: 'bg-amber-500', running: false };
    case 'budget_halted':
      return { label: 'Budget halted', tone: 'text-amber-500 border-amber-500/40 bg-amber-500/10', dot: 'bg-amber-500', running: false };
    case 'loss_halted':
    case 'halted':
      return { label: 'Loss halted', tone: 'text-rose-500 border-rose-500/40 bg-rose-500/10', dot: 'bg-rose-500', running: false };
    case 'error':
      return { label: 'Error', tone: 'text-rose-500 border-rose-500/40 bg-rose-500/10', dot: 'bg-rose-500', running: false };
    default: // stopped | undeployed
      return { label: 'Stopped', tone: 'text-muted-foreground border-border bg-muted/40', dot: 'bg-muted-foreground', running: false };
  }
}

const StatTile: React.FC<{ label: string; children: React.ReactNode; className?: string }> = ({
  label, children, className,
}) => (
  <div className="rounded-md border border-border bg-muted/30 px-3 py-2">
    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className={`text-sm font-semibold mt-0.5 ${className ?? ''}`}>{children}</p>
  </div>
);

const DeploymentCard: React.FC<DeploymentCardProps> = ({ deployment }) => {
  const pauseDeployment = useStrategyLabStore((s) => s.pauseDeployment);
  const resumeDeployment = useStrategyLabStore((s) => s.resumeDeployment);
  const undeployDeployment = useStrategyLabStore((s) => s.undeployDeployment);
  const recommendations = useStrategyLabStore((s) => s.recommendations[deployment.id]);
  const fetchRecommendations = useStrategyLabStore((s) => s.fetchRecommendations);

  const [positions, setPositions] = useState<DeploymentPosition[]>([]);
  const [posLoading, setPosLoading] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const [undeploying, setUndeploying] = useState(false);

  const meta = statusMeta(deployment.status);
  const budgetPct = Math.max(0, Math.min(100, deployment.budget_used_pct ?? 0));

  const loadPositions = useCallback(async () => {
    setPosLoading(true);
    try {
      const data = await strategyLabApi.deploymentPositions(deployment.id);
      setPositions(data.positions || []);
    } catch {
      // Transient; keep the last known positions rather than blanking the table.
    } finally {
      setPosLoading(false);
    }
  }, [deployment.id]);

  useEffect(() => {
    loadPositions();
    fetchRecommendations(deployment.id);
    const t = setInterval(loadPositions, POSITIONS_POLL_MS);
    return () => clearInterval(t);
  }, [loadPositions, fetchRecommendations, deployment.id]);

  const onToggleRunning = async (next: boolean) => {
    setStatusBusy(true);
    if (next) await resumeDeployment(deployment.id);
    else await pauseDeployment(deployment.id);
    setStatusBusy(false);
  };

  const onUndeploy = async () => {
    if (!window.confirm(
      `Undeploy “${deployment.strategy_name ?? 'this strategy'}”? Open positions are flattened.`,
    )) return;
    setUndeploying(true);
    await undeployDeployment(deployment.id);
    setUndeploying(false);
  };

  const pendingRecs = (recommendations || []).filter((r) => r.status === 'pending');

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <CardTitle className="text-base truncate flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-primary shrink-0" />
              {deployment.strategy_name ?? deployment.strategy_id}
            </CardTitle>
          </div>
          <Badge variant="outline" className={`shrink-0 gap-1.5 ${meta.tone}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
            {meta.label}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Budget gauge */}
        <div className="space-y-1">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>Budget used</span>
            <span>
              {fmtUsd(deployment.budget_used_usd)} / {fmtUsd(deployment.budget_usd)} ({fmtPct(budgetPct)})
            </span>
          </div>
          <Progress
            value={budgetPct}
            indicatorClassName={budgetPct >= 90 ? 'bg-rose-500' : budgetPct >= 70 ? 'bg-amber-500' : 'bg-primary'}
          />
        </div>

        {/* Stat tiles */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <StatTile label="P&L today" className={pnlColorClass(deployment.pnl_today)}>
            {fmtSignedUsd(deployment.pnl_today)}
          </StatTile>
          <StatTile label="Open">{deployment.open_position_count}</StatTile>
          <StatTile label="Trades today">{deployment.trades_today}</StatTile>
          <StatTile label="Loss stop" className="text-muted-foreground">
            {fmtUsd(deployment.max_daily_loss_usd)}
          </StatTile>
        </div>

        {/* Positions table */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
              <Activity className="w-3.5 h-3.5" /> Open positions
            </p>
            {posLoading && positions.length === 0 && (
              <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />
            )}
          </div>
          {positions.length === 0 ? (
            <p className="text-xs text-muted-foreground py-2">No open positions.</p>
          ) : (
            <div className="rounded-md border border-border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="h-8">Symbol</TableHead>
                    <TableHead className="h-8 text-right">Qty</TableHead>
                    <TableHead className="h-8 text-right">Entry</TableHead>
                    <TableHead className="h-8 text-right">Last</TableHead>
                    <TableHead className="h-8 text-right">Unrealized</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {positions.map((p) => (
                    <TableRow key={`${p.symbol}-${p.opened_at ?? ''}`}>
                      <TableCell className="py-1.5 font-medium">{p.symbol}</TableCell>
                      <TableCell className="py-1.5 text-right tabular-nums">{p.qty}</TableCell>
                      <TableCell className="py-1.5 text-right tabular-nums">{fmtUsd(p.avg_entry_price)}</TableCell>
                      <TableCell className="py-1.5 text-right tabular-nums">{fmtUsd(p.current_price)}</TableCell>
                      <TableCell className={`py-1.5 text-right tabular-nums ${pnlColorClass(p.unrealized_pnl)}`}>
                        {fmtSignedUsd(p.unrealized_pnl)}
                        {typeof p.unrealized_pnl_pct === 'number' && (
                          <span className="text-[11px] text-muted-foreground ml-1">
                            ({fmtPct(p.unrealized_pnl_pct)})
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>

        {/* Live decision monitor — watch the strategy recurse the market */}
        <StrategyDecisionMonitor deploymentId={deployment.id} running={meta.running} />

        {/* AI recommendations */}
        {pendingRecs.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground">AI recommendations</p>
            {pendingRecs.map((rec) => (
              <RecommendationCard key={rec.id} deploymentId={deployment.id} rec={rec} />
            ))}
          </div>
        )}

        {/* Controls */}
        <div className="flex flex-wrap items-center gap-3 pt-1 border-t border-border">
          <div className="flex items-center gap-2 pt-3">
            {statusBusy ? (
              <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
            ) : (
              <Switch
                checked={meta.running}
                onCheckedChange={onToggleRunning}
                aria-label="Run or pause deployment"
              />
            )}
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Pause className="w-3 h-3" /> {meta.running ? 'Running' : 'Paused'}
            </span>
          </div>
          <div className="flex-1" />
          <div className="flex gap-2 pt-3">
            <Button variant="outline" size="sm" onClick={() => setAdjustOpen(true)}>
              <SlidersHorizontal className="w-3.5 h-3.5" /> Adjust
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-rose-500 hover:text-rose-600"
              disabled={undeploying}
              onClick={onUndeploy}
            >
              {undeploying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Power className="w-3.5 h-3.5" />}
              Undeploy
            </Button>
          </div>
        </div>
      </CardContent>

      <AdjustDeploymentDialog deployment={deployment} open={adjustOpen} onOpenChange={setAdjustOpen} />
    </Card>
  );
};

export default DeploymentCard;
